import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';

import { cx } from '../../lib/cx';
import { FOCUSABLE, trapFocus } from '../../lib/focus-trap';
import type { ModalProps } from './types';

const SIZES = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-3xl' };

// Each alternative of the focusable selector scoped to the body; a plain prefix would only
// apply to the first one.
const BODY_FOCUSABLE = FOCUSABLE.split(',')
  .map((part) => `[data-modal-body] ${part.trim()}`)
  .join(', ');

/**
 * A dialog that takes over the page: focus is trapped inside, Escape and the backdrop close it,
 * focus returns to the opener, and the page behind is hidden from assistive technology.
 */
export const Modal = ({
  open,
  onClose,
  title,
  description,
  actions,
  size = 'md',
  children,
}: ModalProps) => {
  const panel = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    if (!open || !panel.current) {
      return undefined;
    }
    // Start in the content, not on the close button: the first field is what people came for.
    const release = trapFocus(
      panel.current,
      panel.current.querySelector<HTMLElement>(BODY_FOCUSABLE),
    );
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
      release();
    };
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-gray-900/40" onClick={onClose} aria-hidden="true" />
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        className={cx(
          // The dialog never grows past the screen: title and actions stay put, the body scrolls.
          'relative z-10 flex max-h-[calc(100vh-2rem)] w-full flex-col rounded-card bg-white p-6 shadow-overlay outline-none',
          SIZES[size],
        )}
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h2 id={titleId} className="text-base font-semibold text-gray-900">
              {title}
            </h2>
            {description ? (
              <p id={descriptionId} className="mt-1 text-sm text-gray-500">
                {description}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="control-touch flex min-w-11 items-center justify-center rounded-control p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-300"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        <div data-modal-body="" className="min-h-0 flex-1 overflow-y-auto">
          {children}
        </div>
        {actions ? <div className="mt-6 flex justify-end gap-2">{actions}</div> : null}
      </div>
    </div>,
    document.body,
  );
};
